import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { ShoppingCart, Check, Lock } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKS = [
  {
    id: 'basic_math', name: 'Basic Math Pack', price: 0.99, regular: 4.99, discount: 80,
    desc: "The humble beginnings of your Premium Math Journey™.",
    includes: ['+', '−', '1', '2', '3', '4', '5'],
    ea_copy: "The Subtraction Operator is part of the Math Legends Pack™.",
    badge: 'STARTER', category: 'core',
  },
  {
    id: 'advanced_ops', name: 'Advanced Operations Bundle', price: 2.99, regular: 14.99, discount: 80,
    desc: "For the serious mathematician who needs more than five numbers.",
    includes: ['×', '÷', '6', '7', '8', '9', '.'],
    ea_copy: "Unlock the Division Bundle™ — only $2.99. Offer expires when math becomes free.",
    badge: 'POPULAR', category: 'core',
  },
  {
    id: 'ultimate_math', name: 'Ultimate Mathematician Edition', price: 9.99, regular: 49.99, discount: 80,
    desc: "See your answers. Be the mathematician you were born to be.",
    includes: ['=', 'C', '(', ')'],
    ea_copy: "The Equals Sign™ is the crown jewel of Premium Mathematics.",
    badge: 'LEGENDARY', category: 'core', featured: true,
  },
  {
    id: 'negative_numbers', name: 'Negative Numbers Expansion Pack™', price: 4.99, regular: 19.99, discount: 75,
    desc: "Because seeing how much you owe should cost extra.",
    includes: ['Minus sign (as sign)', 'Correct negative results', 'Red − result badge'],
    ea_copy: "Debt is a premium feature. Welcome to financial literacy™.",
    badge: 'EXPANSION', category: 'core',
  },
  {
    id: 'dark_mode_skin', name: 'Dark Mode Skin', price: 0.99, regular: 4.99, discount: 80,
    desc: "Turns the entire calculator dark. Actually functional. We're as surprised as you are.",
    includes: ['Dark Calculator Theme', 'Inverted Button Colors'],
    ea_copy: "Because calculating in the dark hits different.", badge: 'COSMETIC', category: 'addon',
  },
  {
    id: 'sound_effects', name: 'Sound Effects Pack', price: 0.79, regular: 3.99, discount: 80,
    desc: "Every button press makes a satisfying sound. Operators beep higher. Equals plays a victory chord.",
    includes: ['Button Click Tones', 'Operator Sounds', 'Victory Chord on ='],
    ea_copy: "Why calculate in silence?", badge: 'COSMETIC', category: 'addon',
  },
  {
    id: 'history_log', name: 'History Log', price: 1.99, regular: 9.99, discount: 80,
    desc: "See your last 100 calculations in a panel below the calculator. Memory is a Premium feature.",
    includes: ['Last 100 Calculations', 'Expression + Result View'],
    ea_copy: "Memories of past calculations are a Premium feature.", badge: 'UTILITY', category: 'addon',
  },
];

export default function StorePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unlockedPacks, setUnlockedPacks] = useState([]);
  const [loadingPack, setLoadingPack] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    setUnlockedPacks(user.unlocked_packs || []);
    fetch(`${API}/user/unlocks`, { credentials: 'include' })
      .then(r => r.json()).then(d => setUnlockedPacks(d.unlocked_packs || [])).catch(() => {});
  }, [user]);

  const handleBuy = async (packId) => {
    if (!user) {
      const redirectUrl = window.location.origin + '/store';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    if (unlockedPacks.includes(packId)) return;
    setLoadingPack(packId); setError('');
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ pack_id: packId, origin_url: window.location.origin }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Checkout failed'); }
      const data = await res.json();
      window.location.href = data.url;
    } catch (err) { setError(err.message); setLoadingPack(null); }
  };

  const corePacks = PACKS.filter(p => p.category === 'core');
  const addonPacks = PACKS.filter(p => p.category === 'addon');

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950">
      <Helmet>
        <title>DLC Store — Buy Math Packs | CalcByEA™</title>
        <meta name="description" content="Buy calculator DLC packs. Basic Math $0.99, Advanced Operations $2.99, Ultimate Edition $9.99. Unlock addition, multiplication, and the equals sign." />
        <link rel="canonical" href="https://calculatorbyea.com/store" />
        <meta property="og:url" content="https://calculatorbyea.com/store" />
        <meta property="og:title" content="DLC Store — Buy Math Packs | CalcByEA™" />
        <meta property="og:description" content="Buy calculator DLC packs. Basic Math $0.99, Advanced Operations $2.99, Ultimate Edition $9.99. Unlock addition, multiplication, and the equals sign." />
        <meta name="twitter:title" content="DLC Store — Buy Math Packs | CalcByEA™" />
        <meta name="twitter:description" content="Basic Math $0.99. Advanced Operations $2.99. Ultimate Edition $9.99. Unlock your calculator." />
      </Helmet>
      <Navbar />

      {/* Header */}
      <div className="pt-14 border-b-2 border-zinc-950 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="text-[10px] font-bold uppercase tracking-[0.35em] text-zinc-400 mb-4">Premium DLC Store</div>
          <h1 className="font-bebas text-4xl md:text-6xl text-zinc-950 leading-tight mb-4">
            Unlock Your Full<br />Potential™
          </h1>
          <p className="text-zinc-500 text-sm max-w-lg">
            Each pack carefully priced to extract maximum value while delivering the bare minimum required to perform basic arithmetic.
          </p>
          {!user && (
            <div className="mt-5 inline-flex items-center gap-2 border-2 border-zinc-200 text-zinc-400 text-xs font-bold uppercase tracking-widest px-4 py-2">
              <Lock size={11} strokeWidth={3} /> Login required to purchase
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">

        {/* Trust banner */}
        <div className="mb-8 flex items-center gap-4 border-2 border-zinc-950 bg-zinc-950 text-white px-5 py-4">
          <svg className="shrink-0" width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7.5" stroke="#22C55E" strokeWidth="1.5"/><path d="M4.5 8l2.5 2.5 5-5" stroke="#22C55E" strokeWidth="1.5" strokeLinecap="square"/></svg>
          <p className="text-xs leading-relaxed">
            <span className="font-bold uppercase tracking-widest">Yes, this is real. </span>
            <span className="text-zinc-300">Real Stripe payments. Your card is actually charged. The buttons actually unlock. We built the whole thing. Go ahead.</span>
          </p>
        </div>

        {error && (
          <div className="mb-8 border-2 border-zinc-300 text-zinc-500 text-xs font-bold uppercase tracking-widest px-4 py-3" data-testid="store-error">
            {error}
          </div>
        )}

        {/* Core packs */}
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-4 h-0.5 bg-zinc-950" />
            <h2 className="font-bebas text-xl text-zinc-950 tracking-wide">Core Math Packs</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-zinc-950 shadow-[6px_6px_0px_0px_rgba(9,9,11,1)]">
            {corePacks.map((pack) => {
              const owned = unlockedPacks.includes(pack.id);
              const inv = pack.featured;
              return (
                <div
                  key={pack.id}
                  className={`flex flex-col p-5 border-b-2 last:border-b-0 md:border-b-0 md:border-r-2 md:[&:nth-child(2n)]:border-r-2 lg:[&:nth-child(4n)]:border-r-0 border-zinc-950 transition-all duration-150 ${inv ? 'bg-zinc-950 text-white' : 'bg-white text-zinc-950 hover:-translate-y-0.5 hover:shadow-inner'}`}
                  data-testid={`pack-card-${pack.id}`}
                >
                  {/* Badge row */}
                  <div className="flex items-center justify-between mb-5">
                    <span className={`text-[9px] font-bold uppercase tracking-[0.3em] border px-2 py-0.5 ${inv ? 'border-zinc-700 text-zinc-500' : 'border-zinc-200 text-zinc-400'}`}>
                      {pack.badge}
                    </span>
                    {owned && <span className={`text-[10px] font-bold flex items-center gap-1 uppercase tracking-wider ${inv ? 'text-zinc-400' : 'text-zinc-400'}`}><Check size={10} strokeWidth={3} /> Owned</span>}
                  </div>

                  <h3 className={`font-bebas text-xl leading-tight mb-2 ${inv ? 'text-white' : 'text-zinc-950'}`}>{pack.name}</h3>

                  <div className="flex items-baseline gap-2 mb-4">
                    <span className={`font-bebas text-4xl ${inv ? 'text-white' : 'text-zinc-950'}`}>${pack.price}</span>
                    <span className={`text-sm line-through ${inv ? 'text-zinc-600' : 'text-zinc-300'}`}>${pack.regular}</span>
                    <span className={`text-[10px] font-bold border px-1.5 py-0.5 ${inv ? 'border-zinc-700 text-zinc-500' : 'border-zinc-200 text-zinc-400'}`}>{pack.discount}% OFF</span>
                  </div>

                  <p className={`text-xs mb-4 leading-relaxed ${inv ? 'text-zinc-500' : 'text-zinc-500'}`}>{pack.desc}</p>

                  <div className="mb-4">
                    <div className={`text-[9px] font-bold uppercase tracking-widest mb-2 ${inv ? 'text-zinc-600' : 'text-zinc-400'}`}>Includes</div>
                    <div className="flex flex-wrap gap-1">
                      {pack.includes.map(item => (
                        <span key={item} className={`text-xs px-2.5 py-0.5 font-mono font-semibold border ${inv ? 'bg-zinc-900 text-zinc-400 border-zinc-800' : 'bg-zinc-100 text-zinc-600 border-zinc-200'}`}>{item}</span>
                      ))}
                    </div>
                  </div>

                  <p className={`text-[10px] italic mb-5 ${inv ? 'text-zinc-600' : 'text-zinc-400'}`}>"{pack.ea_copy}"</p>

                  <div className="mt-auto">
                    <button
                      onClick={() => handleBuy(pack.id)}
                      disabled={owned || loadingPack === pack.id}
                      className={`w-full flex items-center justify-center gap-2 py-3.5 font-bold uppercase tracking-widest text-sm border-2 transition-all duration-100 ${
                        owned ? (inv ? 'border-zinc-700 text-zinc-600 cursor-default' : 'border-zinc-200 text-zinc-300 cursor-default')
                          : inv
                          ? 'bg-white text-zinc-950 border-white hover:bg-zinc-100 active:bg-zinc-200'
                          : 'bg-zinc-950 text-white border-zinc-950 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none'
                      }`}
                      data-testid={`buy-pack-${pack.id}`}
                    >
                      {owned ? <><Check size={13} strokeWidth={3} /> Owned</>
                        : loadingPack === pack.id ? 'Processing...'
                        : <><ShoppingCart size={13} strokeWidth={2.5} /> {user ? `Buy — $${pack.price}` : 'Login to Purchase'}</>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Add-ons */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-4 h-0.5 bg-zinc-300" />
            <h2 className="font-bebas text-xl text-zinc-400 tracking-wide">Add-Ons & Cosmetics</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 border-2 border-zinc-200">
            {addonPacks.map((pack) => {
              const owned = unlockedPacks.includes(pack.id);
              return (
                <div key={pack.id} className="flex flex-col p-6 bg-white border-r-2 last:border-r-0 border-zinc-200 hover:bg-zinc-50 transition-colors duration-100" data-testid={`pack-card-${pack.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-[0.3em] border border-zinc-200 px-2 py-0.5">{pack.badge}</span>
                    {owned && <Check size={11} strokeWidth={3} className="text-zinc-400" />}
                  </div>
                  <h3 className="font-bebas text-xl text-zinc-950 mb-1 leading-tight">{pack.name}</h3>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="font-bebas text-3xl text-zinc-950">${pack.price}</span>
                    <span className="text-zinc-300 line-through text-xs">${pack.regular}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mb-3 leading-relaxed">{pack.desc}</p>
                  <div className="mb-2">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5">Includes</div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {pack.includes.map(item => (
                        <span key={item} className="text-[9px] px-2 py-0.5 font-mono font-semibold bg-zinc-100 border border-zinc-200 text-zinc-600">{item}</span>
                      ))}
                    </div>
                  </div>
                  <p className="text-[10px] text-zinc-400 italic mb-4">"{pack.ea_copy}"</p>
                  <div className="mt-auto">
                    <button
                      onClick={() => handleBuy(pack.id)}
                      disabled={owned || loadingPack === pack.id}
                      className={`w-full py-3 text-xs font-bold uppercase tracking-widest border-2 transition-all duration-100 ${
                        owned ? 'border-zinc-200 text-zinc-300 cursor-default'
                          : 'border-zinc-300 text-zinc-500 hover:border-zinc-950 hover:text-zinc-950'
                      }`}
                      data-testid={`buy-pack-${pack.id}`}
                    >
                      {owned ? '✓ Owned' : loadingPack === pack.id ? 'Processing...' : user ? `Buy — $${pack.price}` : 'Login to Purchase'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bundle CTA */}
        {unlockedPacks.filter(p => ['basic_math','advanced_ops','ultimate_math'].includes(p)).length < 3 && (
          <div className="border-2 border-zinc-950 bg-white p-8 shadow-[6px_6px_0px_0px_rgba(9,9,11,1)]">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-2">Bundle Deal</div>
                <h3 className="font-bebas text-4xl text-zinc-950">Unlock Everything</h3>
                <p className="text-zinc-500 text-sm mt-1">All 3 core packs — $13.97 total</p>
              </div>
              <button
                onClick={() => handleBuy('bundle_all')}
                disabled={loadingPack === 'bundle_all'}
                className="flex items-center gap-2 bg-zinc-950 text-white font-bold uppercase tracking-widest text-sm px-6 py-3 border-2 border-zinc-950 shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[8px_8px_0px_0px_rgba(9,9,11,1)] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none transition-all duration-100 whitespace-nowrap disabled:opacity-60 disabled:cursor-wait"
                data-testid="bundle-buy-btn"
              >
                {loadingPack === 'bundle_all' ? 'Processing...' : <><ShoppingCart size={14} strokeWidth={2.5} /> {user ? 'Buy Bundle — $13.97' : 'Login to Purchase'}</>}
              </button>
            </div>
          </div>
        )}

        <div className="mt-10 text-center">
          <div className="flex items-center justify-center gap-5 mb-3">
            <a href="/privacy" className="text-[9px] font-bold text-zinc-300 uppercase tracking-widest hover:text-zinc-950 transition-colors">Privacy Policy</a>
            <a href="/terms" className="text-[9px] font-bold text-zinc-300 uppercase tracking-widest hover:text-zinc-950 transition-colors">Terms of Service</a>
            <a href="/contact" className="text-[9px] font-bold text-zinc-300 uppercase tracking-widest hover:text-zinc-950 transition-colors">Contact</a>
          </div>
          <p className="text-[9px] font-bold text-zinc-300 uppercase tracking-widest">
            All sales are final. No refunds. EA is a trademark of Electronic Arts. This is a parody.
          </p>
          <p className="text-[9px] font-medium text-zinc-300 mt-1">
            <a href="https://itorzo.com" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-500 transition-colors">An Itorzo product</a>
          </p>
        </div>
      </div>
    </div>
  );
}
